import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  ArrowLeft, 
  History,
  Home,
  Target,
  Calendar,
  User,
  TrendingUp,
  TrendingDown,
  Pencil
} from "lucide-react";
import EditEvaluationDialog from "@/components/EditEvaluationDialog";
import { 
  Radar, 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  ResponsiveContainer 
} from 'recharts';

export default function PlayerHistoryPage({ evaluator }) {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [averages, setAverages] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [allEvaluators, setAllEvaluators] = useState([]);
  const [editingEvaluation, setEditingEvaluation] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const isAdmin = evaluator?.role === 'admin';

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/players/${playerId}/history`),
        axios.get(`${API}/players/${playerId}/averages`),
      ];
      if (isAdmin) requests.push(axios.get(`${API}/evaluators`));
      
      const results = await Promise.all(requests);
      setPlayer(results[0].data.player);
      setEvaluations(results[0].data.evaluations);
      setAverages(results[1].data);
      if (results[2]) setAllEvaluators(results[2].data || []);
    } catch (error) {
      toast.error("Erro ao carregar histórico");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [playerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-400";
    if (score >= 5) return "text-yellow-400";
    return "text-red-400";
  };

  const getPlayerName = () => {
    if (!player) return '';
    if (player.first_name) return `${player.first_name} ${player.last_name}`;
    return player.name || '';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const radarData = averages ? [
    { skill: 'Ataque', value: averages.averages?.ataque || 0, fullMark: 10 },
    { skill: 'Passe', value: averages.averages?.passe || 0, fullMark: 10 },
    { skill: 'Defesa', value: averages.averages?.defesa || 0, fullMark: 10 },
    { skill: 'Saque', value: averages.averages?.saque || 0, fullMark: 10 },
    { skill: 'Bloqueio', value: averages.averages?.bloqueio || 0, fullMark: 10 },
    { skill: 'Toque', value: averages.averages?.toque || 0, fullMark: 10 },
    { skill: 'Skill', value: averages.averages?.skill || 0, fullMark: 10 },
  ] : [];

  const getOverallAverage = () => {
    if (!averages?.averages) return 0;
    const values = Object.values(averages.averages);
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  const getTrend = () => {
    if (evaluations.length < 2) return null;
    const latest = evaluations[0]?.overall || 0;
    const previous = evaluations[1]?.overall || 0;
    return latest - previous;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                Histórico de Avaliações
              </h1>
              <p className="text-sm text-muted-foreground">{getPlayerName()}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Player Info Card */}
          <Card className="bg-card border-border/50 shadow-xl lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Informações do Jogador
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center text-center">
                {player?.photo_url ? (
                  <img 
                    src={player.photo_url} 
                    alt={getPlayerName()}
                    className="w-24 h-24 rounded-full object-cover border-4 border-primary mb-4"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <User className="h-12 w-12 text-primary" />
                  </div>
                )}
                <h2 className="text-2xl font-bold">{getPlayerName()}</h2>
                <div className="flex gap-2 mt-2">
                  {player?.yankee && <Badge variant="secondary">{player.yankee}</Badge>}
                  <Badge variant="outline">{player?.position}</Badge>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <span className={`text-4xl font-black ${getScoreColor(parseFloat(getOverallAverage()))}`}>
                    {getOverallAverage()}
                  </span>
                  {getTrend() !== null && (
                    <span className={`flex items-center ${getTrend() >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {getTrend() >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                      <span className="text-sm ml-1">{getTrend() >= 0 ? '+' : ''}{getTrend().toFixed(1)}</span>
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {averages?.total_evaluations || 0} avaliações
                </p>
                
                {/* Botão Avaliar - visível em desktop */}
                <Button
                  onClick={() => navigate(`/evaluate?player=${playerId}`)}
                  className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="evaluate-player-btn"
                >
                  <Target className="h-4 w-4 mr-2" />
                  Avaliar Este Jogador
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Radar Chart */}
          <Card className="bg-card border-border/50 shadow-xl lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight">
                Gráfico de Habilidades
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#27272A" />
                    <PolarAngleAxis dataKey="skill" tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: '#a1a1aa' }} />
                    <Radar
                      name="Habilidades"
                      dataKey="value"
                      stroke="#007AFF"
                      fill="#007AFF"
                      fillOpacity={0.3}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Evaluation History Table */}
        <Card className="bg-card border-border/50 shadow-xl mt-8">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Histórico de Avaliações ({evaluations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {evaluations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma avaliação encontrada para este jogador.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="text-xs font-bold uppercase tracking-widest">Data</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest">Avaliador</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Ataque</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Passe</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Defesa</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Saque</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Bloqueio</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Toque</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Skill</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Média</TableHead>
                      {isAdmin && (
                        <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Ações</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {evaluations.map((eval_, index) => (
                      <TableRow key={eval_.id} className="border-border/50">
                        <TableCell className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {formatDate(eval_.created_at)}
                        </TableCell>
                        <TableCell>{eval_.evaluator_name || eval_.evaluator?.name || 'Desconhecido'}</TableCell>
                        <TableCell className={`text-center font-bold ${getScoreColor(eval_.skills?.ataque)}`}>
                          {eval_.skills?.ataque}
                        </TableCell>
                        <TableCell className={`text-center font-bold ${getScoreColor(eval_.skills?.passe)}`}>
                          {eval_.skills?.passe}
                        </TableCell>
                        <TableCell className={`text-center font-bold ${getScoreColor(eval_.skills?.defesa)}`}>
                          {eval_.skills?.defesa}
                        </TableCell>
                        <TableCell className={`text-center font-bold ${getScoreColor(eval_.skills?.saque)}`}>
                          {eval_.skills?.saque}
                        </TableCell>
                        <TableCell className={`text-center font-bold ${getScoreColor(eval_.skills?.bloqueio)}`}>
                          {eval_.skills?.bloqueio}
                        </TableCell>
                        <TableCell className={`text-center font-bold ${getScoreColor(eval_.skills?.toque)}`}>
                          {eval_.skills?.toque}
                        </TableCell>
                        <TableCell className={`text-center font-bold ${getScoreColor(eval_.skills?.skill)}`}>
                          {eval_.skills?.skill}
                        </TableCell>
                        <TableCell className={`text-center font-bold text-lg ${getScoreColor(eval_.overall)}`}>
                          {eval_.overall}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-blue-400 border-blue-500/40 hover:bg-blue-500/10"
                              onClick={() => {
                                setEditingEvaluation(eval_);
                                setEditDialogOpen(true);
                              }}
                              data-testid={`edit-eval-history-${eval_.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5 mr-1" />
                              Editar
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Evaluation Dialog */}
      {isAdmin && (
        <EditEvaluationDialog
          evaluation={editingEvaluation}
          evaluators={allEvaluators}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setEditingEvaluation(null);
          }}
          onSaved={fetchData}
        />
      )}

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/players')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <User className="h-5 w-5" />
          <span className="text-xs">Jogadores</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/evaluate')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
